"use client";

import { type ComponentPropsWithRef, Suspense } from "react";

import { Separator } from "@radix-ui/themes";
import { useTranslations } from "next-intl";

import { priceFormat } from "@/shared.utils/number-format";
import { PrimRadioButtons } from "@/shared-web.ui/prim.radio-buttons";
import { Text } from "@/shared-web.ui/ui.text";
import { useVariantState } from "@/stf.m.product/feat.product-detail";

import {
  type ServerDataOutput,
  extractVariantBoxSize,
  formatWeight,
  humanWeight,
  useSubscriptionSelectorActions,
} from "../data.subscription-data";
import * as SelectorContainer from "../prim.selector-container";

interface BoxSizeCardProps {
  price: number;
  sizeLabel?: null | string;
  unit: string;
  value: number;
}
export const BoxSizeCard = (props: BoxSizeCardProps) => {
  const { value: weightValue, unit, price, sizeLabel } = props;
  const t = useTranslations("stf_m_subscription.screen_subscription.box_size_selector");

  return (
    <div className="grid p-2">
      <Text size="5" weight="medium" className="text-center">
        {sizeLabel || "Box"}
      </Text>
      <Text size="2" weight="medium">
        <Text as="span" size="1" color="gray" mr="1">≈</Text>
        {formatWeight(weightValue)}
        <Text as="span" size="1" color="gray" ml="1">{unit}</Text>
      </Text>
      <Text size="1" color="gray">
        {t("per_box")}
      </Text>
      <div className="flex justify-center">
        <Separator
          orientation="horizontal"
          size="1"
          className="my-2"
        />
      </div>
      <div className="flex justify-center">
        <Text
          weight="medium"
          className="bg-accent-2 px-3 py-2 rounded-3"
          semanticColor="accent"
        >
          {priceFormat(price)}
        </Text>
      </div>
    </div>
  );
};

interface BoxSizeSelectorDisplayProps extends Pick<ComponentPropsWithRef<typeof PrimRadioButtons.Root>, "onValueChange" | "value"> {
  data: ServerDataOutput;
  selectedPackage?: string | undefined;
}
const BoxSizeSelectorDisplay = (props: BoxSizeSelectorDisplayProps) => {
  const { value, onValueChange, data, selectedPackage } = props;
  const {
    subscriptionVariant: { boxSizeVariant },
    stickerPriceData: { packageBoxStickerPrice },
  } = data;
  const { variantOptionsArray, variantOptions } = boxSizeVariant;

  const t = useTranslations("stf_m_subscription.screen_subscription.box_size_selector");

  return (
    <SelectorContainer.Root>
      <SelectorContainer.Title>
        {t("select_your_box_size")}
      </SelectorContainer.Title>
      <SelectorContainer.Body>
        <PrimRadioButtons.Root
          className={`
            grid grid-cols-2
            xxs:grid-cols-3
          `}
          value={value}
          onValueChange={onValueChange}
        >
          {variantOptionsArray.map(({ optionKey }) => {
            const options = variantOptions[optionKey];
            const boxStickerPrice = (selectedPackage && packageBoxStickerPrice[selectedPackage])
              ? packageBoxStickerPrice[selectedPackage][optionKey] || 0
              : 0;
            const { value: weightValue, unit } = humanWeight({
              grams: Number(extractVariantBoxSize(options.optionName)),
            });
            return (
              <PrimRadioButtons.Item value={optionKey} key={optionKey}>
                <BoxSizeCard value={weightValue} unit={unit} price={boxStickerPrice} sizeLabel={options.sizeLabel} />
              </PrimRadioButtons.Item>
            );
          })}
        </PrimRadioButtons.Root>
      </SelectorContainer.Body>
      <SelectorContainer.Remarks>
        {t("beans_weight_may_vary")}
      </SelectorContainer.Remarks>
    </SelectorContainer.Root>
  );
};

interface BoxSizeSelectorWithParamsProps {
  data: ServerDataOutput;
}
const BoxSizeSelectorWithParams = (props: BoxSizeSelectorWithParamsProps) => {
  const { data } = props;
  const { opts } = useVariantState();
  const { setVal, getVal } = useSubscriptionSelectorActions({ data });
  const {
    subscriptionData: {
      subscriptionMeta: { variantMapper },
    },
  } = data;
  const selectedPackage = getVal("subscriptionPackage");

  return (
    <BoxSizeSelectorDisplay
      data={data}
      selectedPackage={selectedPackage}
      value={opts?.[variantMapper.boxSize]}
      onValueChange={(v) => { setVal("boxSize", v); }}
    />
  );
};

interface BoxSizeSelectorProps {
  data: ServerDataOutput;
}
export const BoxSizeSelector = (props: BoxSizeSelectorProps) => {
  const { data } = props;
  return (
    <Suspense fallback={<BoxSizeSelectorDisplay data={data} />}>
      <BoxSizeSelectorWithParams data={data} />
    </Suspense>
  );
};
